import {
  Flex,
  Grid,
  Heading,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";

import CaliberItem from "../components/CaliberItem/CaliberItem";
import BreadcrumbCustom from "../components/BreadcrumbCustom/BreadcrumbCustom";
import { memo, useEffect, useMemo } from "react";
import { getTitle } from "../utils/getCategoryName";
import CaliberSkeleton from "../skeletons/CaliberSkeleton/CaliberSkeleton";

import { Helmet } from "react-helmet";
import {
  allCategoryInfoAtom,
  calibersLoadingStatusAtom,
  categoryAtom,
  fetchCalibers,
} from "../atom/calibers";
import { allBrandInfoAtom, fetchBrands } from "../atom/brands";
import { brandSEOAtom, fetchBrandSEOAtom } from "../atom/brandSEO";
import { useAtom } from "jotai";
import { useFacetMeta } from "../hooks/useFacetMeta";
import { parseSeoUrl } from "../utils/seoUrlMapping";
import { URL_TYPES } from "../constants/seoUrlPatterns";

import VendorBanner from "../components/VendorBanner/VendorBanner";

const CategoriesPage = () => {
  const location = useLocation();
  const [, setBrandSEO] = useAtom(fetchBrandSEOAtom);
  const [brandSEO] = useAtom(brandSEOAtom);

  // Check if this is a new SEO URL
  const isSeoUrl = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    return pathSegments[0] && Object.values(URL_TYPES).includes(pathSegments[0]);
  }, [location.pathname]);

  // Parse SEO URL if applicable
  const seoUrlData = useMemo(() => {
    if (isSeoUrl) {
      try {
        return parseSeoUrl(location.pathname, location.search);
      } catch (error) {
        console.error('Error parsing SEO URL:', error);
        return null;
      }
    }
    return null;
  }, [isSeoUrl, location.pathname, location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isSeoUrl && seoUrlData) {
      // Handle SEO URL
      if (seoUrlData.brand) {
        setBrandSEO(seoUrlData.brand);
      }
    } else {
      // Legacy URL handling
      const locState = location.pathname;
      const slug = locState?.split("/").pop();
      const isProductPage = locState?.includes("product");
      const isAguilaPage = locState?.includes("aguila");

      if (isProductPage && slug) {
        setBrandSEO("aguila");
      } else if (slug) {
        if (isAguilaPage) {
          setBrandSEO("aguila");
        }
      }
    }

    // eslint-disable-next-line
  }, [location.pathname, isSeoUrl, seoUrlData]);

  const getSEOData = () => {
    const locState = location.state;

    // Check for SEO URL patterns first
    const isHandgunAmmo = isSeoUrl ? seoUrlData?.weapon_category === 'handgun-ammo' : location.pathname?.includes("handgun-ammo");
    const isRifleAmmo = isSeoUrl ? seoUrlData?.weapon_category === 'rifle-ammo' : location.pathname?.includes("rifle-ammo");
    const isShotgunAmmo = isSeoUrl ? seoUrlData?.weapon_category === 'shotgun-ammo' : location.pathname?.includes("shotgun-ammo");
    const isRimfireAmmo = isSeoUrl ? seoUrlData?.weapon_category === 'rimfire-ammo' : location.pathname?.includes("rimfire-ammo");
    
    // Legacy check for Aguila
    const isAguilaPage = !isSeoUrl && locState?.includes("aguila");

    const defaultMetaDescription =
      "Bullet Scout, your premier destination for finding the right ammunition for your firearm needs. With our comprehensive search engine, you can explore a wide array of ammunition options, ensuring you always hit your mark.";

    const seoData = {
      product: {
        title: brandSEO?.title_tag || "Bullet Scout",
        metaDescription: brandSEO?.meta_desc || defaultMetaDescription,
      },
      aguila: {
        title: brandSEO?.title_tag || "Bullet Scout",
        metaDescription: brandSEO?.meta_desc || defaultMetaDescription,
      },
      handgunAmmo: {
        title: "Find The Cheapest Handgun Ammo Ammo Online | BulletScout",
        metaDescription:
          "The new premier search engine for finding the best prices for handgun ammunition online, with no hidden fees or deceptive shipping costs.",
      },
      rifleAmmo: {
        title: "Find The Cheapest Rifle Ammo Ammo Online | BulletScout",
        metaDescription:
          "The new premier search engine for finding the best prices for rifle ammunition online, with no hidden fees or deceptive shipping costs.",
      },
      shotgunAmmo: {
        title: "Find The Cheapest Shotgun Ammo Ammo Online | BulletScout",
        metaDescription:
          "The new premier search engine for finding the best prices for shotgun ammunition online, with no hidden fees or deceptive shipping costs.",
      },
      rimfireAmmo: {
        title: "Find The Cheapest Rimfire Ammo Ammo Online | BulletScout",
        metaDescription:
          "The new premier search engine for finding the best prices for rimfire ammunition online, with no hidden fees or deceptive shipping costs.",
      },
      default: {
        title: brandSEO?.title_tag || "Bullet Scout",
        metaDescription: brandSEO?.meta_desc || defaultMetaDescription,
      },
    };

    const currentSEOData = isAguilaPage
      ? seoData.aguila
      : isHandgunAmmo
        ? seoData.handgunAmmo
        : isRifleAmmo
          ? seoData.rifleAmmo
          : isShotgunAmmo
            ? seoData.shotgunAmmo
            : isRimfireAmmo
              ? seoData.rimfireAmmo
              : seoData.default;

    return currentSEOData;
  };

  const [allCategoryInfo] = useAtom(allCategoryInfoAtom);
  const [allBrandInfo] = useAtom(allBrandInfoAtom);

  const [calibersLoading, setCalibersLoading] = useAtom(
    calibersLoadingStatusAtom,
  );

  const [category, setCategory] = useAtom(categoryAtom);

  const [, setCalibers] = useAtom(fetchCalibers);
  const [, setBrands] = useAtom(fetchBrands);

  useEffect(() => {
    if (location.pathname.includes("aguila")) {
      setBrands("10");
    } else {
      if (allCategoryInfo.length === 0) {
        setCalibers(location.pathname);
      }
    }

    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    setCalibersLoading("loading");
    setCategory(
      allCategoryInfo.filter((category) =>
        category.weapon_category
          .toLowerCase()
          .includes(
            getTitle(location.pathname).replace(" Ammo", "").toLowerCase(),
          ),
      )[0],
    );
    setCalibersLoading("idle");

    // eslint-disable-next-line
  }, [location.pathname]);

  const GridWrapper = ({ children }) => (
    <Grid
      display={"grid"}
      gap={{ base: "9px", md: "21px" }}
      flexWrap={"wrap"}
      w={"100%"}
      mb={{ base: "77px", md: "94px" }}
      templateColumns={{
        base: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(4, 1fr)",
        xl: "repeat(5, 1fr)",
        ".5xl": "repeat(6, 1fr)",
      }}
      justifyItems={"center"}
    >
      {children}
    </Grid>
  );

  return (
    <Flex
      pt={{ base: "22px", lg: "43px" }}
      maxW={"1240px"}
      w={"100%"}
      mx={"auto"}
      px={{ base: "22px", lg: "10px" }}
      flexDirection={"column"}
    >
      <Helmet
        onChangeClientState={() => {
          const metaDescriptionElement = document.querySelector(
            'meta[name="description"]',
          );

          if (metaDescriptionElement) {
            metaDescriptionElement.setAttribute(
              "content",
              getSEOData().metaDescription,
            );
          }
        }}
      >
        <title>{getSEOData().title}</title>
        
      </Helmet>
      <BreadcrumbCustom />

      {location.pathname.includes("aguila") ? (
        <>
          <VendorBanner />

          <Tabs pt={{ base: 4, md: 6, lg: 8 }}>
            {!!allBrandInfo.length ? (
              <TabList>
                {allBrandInfo.map(({ weapon_category }) => (
                  <Tab key={weapon_category}>
                    <Heading
                      fontSize={{ base: "sm", sm: "base" }}
                      fontFamily={"mainFont"}
                      fontWeight={"500"}
                    >
                      {`${
                        weapon_category.charAt(0).toUpperCase() +
                        weapon_category.slice(1)
                      } Ammo`}
                    </Heading>
                  </Tab>
                ))}
              </TabList>
            ) : (
              <Skeleton w={"350px"} h={"40px"} mb={{ base: 6, md: 8 }} />
            )}

            {!!allBrandInfo.length ? (
              <TabPanels>
                {allBrandInfo.map(({ calibers }, index) => (
                  <TabPanel key={index}>
                    <GridWrapper>
                      {calibers.map((caliber) => (
                        <CaliberItem
                          key={caliber.caliber}
                          path={location.pathname}
                          category={caliber}
                          categoryName={allBrandInfo[index].weapon_category}
                        />
                      ))}
                    </GridWrapper>
                  </TabPanel>
                ))}
              </TabPanels>
            ) : (
              <GridWrapper>
                {[...Array(12)].map((item, index) => (
                  <CaliberSkeleton key={index} />
                ))}
              </GridWrapper>
            )}
          </Tabs>
        </>
      ) : (
        <>
          {category?.weapon_category ? (
            <Heading
              w={"100%"}
              textAlign={"center"}
              fontSize={"25px"}
              fontFamily={"mainFont"}
              fontWeight={"600"}
              mb={{ base: 6, md: 8 }}
            >
              {`${category?.weapon_category} Ammo`}
            </Heading>
          ) : (
            <Skeleton
              w={"250px"}
              h={"40px"}
              mx={"auto"}
              mb={{ base: 6, md: 8 }}
            />
          )}

          <GridWrapper>
            {category?.calibers && calibersLoading === "idle"
              ? category.calibers.map((caliber) => (
                  <CaliberItem
                    key={caliber.caliber}
                    path={location.pathname}
                    category={caliber}
                    categoryName={category.weapon_category}
                  />
                ))
              : [...Array(30)].map((item, index) => (
                  <CaliberSkeleton key={index} />
                ))}
          </GridWrapper>
        </>
      )}
    </Flex>
  );
};

export default memo(CategoriesPage);
